/**
 * O que faz: Coleta nome, WhatsApp e, quando necessário, tipo de clínica antes de abrir conversa comercial no WhatsApp.
 * Onde usa: CTAs de artigos, hubs e comparativos públicos do Gens CRM.
 * Conexões: Recebe contexto do conteúdo para montar mensagem pré-preenchida; não grava dados nesta primeira fase.
 * Tabelas: Não interage com Supabase; origem fica pronta para futura persistência de leads.
 * Data: 12/05/2026
 */

import { useMemo, useState } from "react";
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type LeadCaptureFormProps = {
  ctaContext: string;
  sourceUrl: string;
  id?: string;
  submitLabel?: string;
  variant?: "default" | "compact";
};

const WHATSAPP_NUMBER = "5583996886297";

export const LeadCaptureForm = ({ ctaContext, sourceUrl, id, submitLabel, variant = "default" }: LeadCaptureFormProps) => {
  const isCompact = variant === "compact";
  const [nome, setNome] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [tipoClinica, setTipoClinica] = useState(isCompact ? "Não informado no formulário compacto" : "");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);

  const mensagem = useMemo(
    () =>
      `Ola, meu nome e ${nome || "[nome]"}. Vim pelo ${ctaContext} e quero entender como o Gens CRM pode ajudar minha clinica. Tipo de clinica: ${tipoClinica || "[tipo]"}. Origem: ${sourceUrl}.`,
    [ctaContext, nome, sourceUrl, tipoClinica],
  );

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErro("");

    // Motivo: evita enviar contato sem identificacao minima para o time comercial.
    // Consequencia: sem nome valido, o formulario interrompe e mostra erro claro.
    if (nome.trim().length < 2) {
      setErro("Informe seu nome para continuarmos pelo WhatsApp.");
      return;
    }

    // Motivo: numeros curtos quebram o redirecionamento no WhatsApp e impedem continuidade.
    // Consequencia: sem DDD e telefone validos, mantemos o usuario na pagina para corrigir.
    const telefoneLimpo = whatsapp.replace(/\D/g, "");
    if (telefoneLimpo.length < 10) {
      setErro("Informe um WhatsApp valido com DDD.");
      return;
    }

    // Motivo: CTAs compactos do artigo priorizam contato rápido e seguem o layout da página de blog.
    // Regra de negócio: quando o campo aparece em CTAs completos, ele continua obrigatório para qualificar o lead.
    if (!isCompact && !tipoClinica.trim()) {
      setErro("Informe o tipo de clínica para personalizarmos o atendimento.");
      return;
    }

    setEnviando(true);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensagem)}`;
    window.location.assign(url);
  };

  return (
    <form id={id} className={`public-lead-form ${isCompact ? "public-lead-form-compact" : ""}`} onSubmit={handleSubmit}>
      <div>
        <Label htmlFor="lead-nome">Nome</Label>
        <Input id="lead-nome" placeholder="Seu nome" value={nome} onChange={(event) => setNome(event.target.value)} />
      </div>
      <div>
        <Label htmlFor="lead-whatsapp">WhatsApp (com DDD)</Label>
        <Input
          id="lead-whatsapp"
          inputMode="tel"
          placeholder="(83) 99999-0000"
          value={whatsapp}
          onChange={(event) => setWhatsapp(event.target.value)}
        />
      </div>
      {!isCompact ? (
        <div>
          <Label htmlFor="lead-tipo-clinica">Tipo de clínica</Label>
          <Input id="lead-tipo-clinica" value={tipoClinica} onChange={(event) => setTipoClinica(event.target.value)} />
        </div>
      ) : null}

      <p className="public-form-consent">
        Ao continuar, voce autoriza contato comercial pelo WhatsApp sobre o Gens CRM.
      </p>

      {erro ? <p className="public-form-error">{erro}</p> : null}

      <Button type="submit" disabled={enviando} className="public-primary-button">
        <MessageCircle size={18} />
        {enviando ? "Abrindo WhatsApp..." : submitLabel ?? "Chamar no WhatsApp"}
      </Button>
    </form>
  );
};
